// src/edu-world/data/persistence.ts
// IndexedDB persistence for world state with localStorage fallback.
// Fails silently with console.warn — never displays errors to child.
// Requirements: 2.1, 2.2, 2.3, 2.4, 2.5

import type { WorldState } from '../types/index';

const DB_NAME = 'edu-world';
const STORE_NAME = 'state';
const STATE_KEY = 'world-state';
const LS_KEY = 'edu-world-state';

// ─── Default World State ───

export function getDefaultWorldState(): WorldState {
  return {
    missions: {},
    impacts_applied: [],
    player_position: { x: 0, y: 0 },
    current_zone: 'parque',
  };
}

// ─── IndexedDB Helpers ───

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function idbPut(db: IDBDatabase, key: string, value: unknown): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(value, key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

function idbGet(db: IDBDatabase, key: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ─── localStorage Fallback ───

function saveToLocalStorage(state: WorldState): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    console.warn('[EduWorld] localStorage save failed');
  }
}

function loadFromLocalStorage(): WorldState | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as WorldState;
  } catch {
    console.warn('[EduWorld] localStorage load failed');
    return null;
  }
}

// ─── IndexedDB Available Check ───

function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

// ─── Public API ───

/**
 * Saves the world state to IndexedDB (or localStorage as fallback).
 * Completes within 2 seconds on mission completion.
 * Fails silently — never displays error to child.
 */
export async function saveWorldState(state: WorldState): Promise<void> {
  if (!isIndexedDBAvailable()) {
    saveToLocalStorage(state);
    return;
  }

  try {
    const db = await openDatabase();
    await idbPut(db, STATE_KEY, state);
    db.close();
  } catch (error) {
    console.warn('[EduWorld] IndexedDB save failed, falling back to localStorage', error);
    saveToLocalStorage(state);
  }
}

/**
 * Loads the world state from IndexedDB (or localStorage as fallback).
 * Completes within 3 seconds on boot.
 * Returns null if no saved state exists.
 * Fails silently — never displays error to child.
 */
export async function loadWorldState(): Promise<WorldState | null> {
  if (!isIndexedDBAvailable()) {
    return loadFromLocalStorage();
  }

  try {
    const db = await openDatabase();
    const data = await idbGet(db, STATE_KEY);
    db.close();

    if (data && isValidWorldState(data)) {
      return data as WorldState;
    }

    // If IndexedDB data is missing or invalid, try localStorage recovery
    if (!data) {
      return loadFromLocalStorage();
    }

    console.warn('[EduWorld] IndexedDB state invalid, attempting localStorage recovery');
    return loadFromLocalStorage();
  } catch (error) {
    console.warn('[EduWorld] IndexedDB load failed, falling back to localStorage', error);
    return loadFromLocalStorage();
  }
}

// ─── Validation ───

function isValidWorldState(data: unknown): data is WorldState {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;

  if (typeof obj.missions !== 'object' || obj.missions === null) return false;
  if (!Array.isArray(obj.impacts_applied)) return false;
  if (
    !obj.player_position ||
    typeof (obj.player_position as Record<string, unknown>).x !== 'number' ||
    typeof (obj.player_position as Record<string, unknown>).y !== 'number'
  ) {
    return false;
  }
  if (typeof obj.current_zone !== 'string') return false;

  return true;
}
